import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { EMPTY, Observable, timer } from 'rxjs';
import { catchError, first, switchMap } from 'rxjs/operators';
import { BuildDetails } from '../models/build-details.model';
import { UpdateAppDialogComponent } from '../components/update-app-dialog/update-app-dialog.component';

/** Delay between two deploy stamp checks. */
const POLL_INTERVAL_MS = 60_000;

/**
 * Detects a newer console deploy by polling `assets/build-details.json`.
 * Opens a blocking dialog so the user reloads and picks up the new Angular bundle.
 */
@Injectable({ providedIn: 'root' })
export class UpToDateService {
  private readonly http = inject(HttpClient);
  private readonly dialog = inject(MatDialog);

  /**
   * Reads the stamp of the build being loaded, then watches for a newer one.
   * @returns {void}
   */
  constructor() {
    this.fetch()
      .pipe(catchError(() => EMPTY))
      .subscribe((baseline) => this.poll(baseline));
  }

  /**
   * Loads the deploy stamp from the static asset (bypassing intermediate caches).
   * The unique query param is what actually defeats the caches: on a fixed asset name, a
   * `Cache-Control` request header alone can be ignored by the browser or the CDN, and the
   * stale stamp would hide the new deploy.
   * @returns Stream emitting the build details currently served
   */
  private fetch(): Observable<BuildDetails> {
    return this.http.get<BuildDetails>(`assets/build-details.json?t=${Date.now()}`, {
      headers: new HttpHeaders({ 'Cache-Control': 'no-cache' }),
    });
  }

  /**
   * Polls the deploy stamp until it differs from the baseline, then prompts a reload.
   * Failed requests are swallowed so a transient network error cannot stop the polling.
   * @param baseline - Build details of the bundle the user is currently running
   * @returns {void}
   */
  private poll(baseline: BuildDetails): void {
    timer(POLL_INTERVAL_MS, POLL_INTERVAL_MS)
      .pipe(
        switchMap(() => this.fetch().pipe(catchError(() => EMPTY))),
        first((details) => details.timestamp !== baseline.timestamp),
      )
      .subscribe(() => {
        this.dialog.open(UpdateAppDialogComponent, {
          closeOnNavigation: false,
          disableClose: true,
        });
      });
  }
}
